import { BaseLink } from "@design-system/components/link";
import { cn } from "@design-system/utils";
import { trans } from "@mongez/localization";
import { ChevronLeft, Trophy } from "lucide-react";
import { useHallOfFame } from "../hooks/use-hall-of-fame.hook";
import { HallOfFameCard } from "./HallOfFameCard";
import { HallOfFameGradeChips } from "./HallOfFameGradeChips";

/**
 * Home «لوحة الشرف»: horizontal carousel of the top 10 certificates.
 * Hidden entirely when the platform has nothing on the hall of fame.
 *
 * @example
 * <HallOfFameSection className="container mx-auto px-4" />
 */
export function HallOfFameSection({ className }: { className?: string }) {
  const hall = useHallOfFame();

  if (hall.isHidden) return null;

  return (
    <section
      aria-labelledby="home-hall-of-fame-title"
      className={cn("space-y-4", className)}
    >
      <div className="flex items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border-b-4 border-amber-600 bg-amber-400 text-amber-950 shadow-sm">
          <Trophy className="size-6" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2
            id="home-hall-of-fame-title"
            className="text-xl font-black text-slate-950 dark:text-white"
          >
            {trans("certificates.hallOfFame.title")}
          </h2>
          <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
            {trans("certificates.hallOfFame.subtitle")}
          </p>
        </div>
        <BaseLink
          to={hall.viewAllUrl}
          className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-full px-3 text-sm font-black text-amber-700 hover:bg-amber-50 hover:no-underline dark:text-amber-300 dark:hover:bg-amber-950"
        >
          {trans("certificates.hallOfFame.viewAll")}
          <ChevronLeft className="size-4 ltr:rotate-180" aria-hidden="true" />
        </BaseLink>
      </div>

      <HallOfFameGradeChips
        grades={hall.grades}
        selected={hall.grade}
        onSelect={hall.selectGrade}
      />

      {hall.certificates.length === 0 ? (
        <p className="rounded-3xl border-2 border-dashed border-slate-200 p-6 text-center font-bold text-slate-500 dark:border-slate-700 dark:text-slate-400">
          {trans("certificates.hallOfFame.empty")}
        </p>
      ) : (
        <ul
          aria-busy={hall.isLoading}
          className={cn(
            "-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-3 transition-opacity",
            hall.isLoading && "opacity-60",
          )}
        >
          {hall.certificates.map((entry) => (
            <li key={entry.id} className="w-64 shrink-0 snap-start sm:w-72">
              <HallOfFameCard entry={entry} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
